/**
 * Designer sandbox — `/sandbox/designer`.
 *
 * Opens the 3D designer straight away on a preset: no wizard, no splash.
 * Built for iterating on the designer under the dev server (`npm start`), where
 * a saved code change shows up without a build.
 *
 * The designer itself is `DesignerHost` (shared with the results-page modal).
 * This page adds the preset chooser, and keeps the plan, the focus room, the
 * locked wall and the selection in localStorage, so neither a hot reload nor a
 * page refresh loses your place. (Undo history is not kept across a reload.)
 *
 * Open to anyone under the dev server; super-admin only in a built app (the
 * route guard lives in App.tsx).
 */

import React, { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, RotateCcw } from 'lucide-react';
import { DesignerHost, NO_UI, designerControlStyle as controlStyle } from '../components/designer/DesignerHost';
import { IconTextButton } from '../components/designer/chrome/IconTextButton';
import { usePropLibrary } from '../components/designer/usePropLibrary';
import { DESIGNER_PRESETS, DEFAULT_PRESET_ID, getPreset } from '../components/designer/presets';
import { designerDocument } from '../components/designer/designerDocument';
import { SandboxState, loadSandboxState, saveSandboxState } from '../components/designer/sandboxStorage';
import { DESIGNER_STRINGS as S } from '../components/designer/strings';
import { validateRoom } from '../calculations/sections';
import { EMPTY_PLAN, planFromSingleRoom, setCeilingProfile } from '../calculations/plan';

/** Not a room preset: an empty sheet to draw on. */
const BLANK_PRESET_ID = 'blank';

function freshState(presetId: string): SandboxState {
  if (presetId === BLANK_PRESET_ID) {
    return { presetId, plan: EMPTY_PLAN, focusRoomId: null, plannerOpen: true, ui: NO_UI };
  }
  const preset = getPreset(presetId);
  const doc = preset.build();
  let plan = planFromSingleRoom(doc.dims, doc.room);
  if (doc.ceilingProfile) {
    const shaped = setCeilingProfile(plan, [plan.rooms[0].id], doc.ceilingProfile);
    if (shaped.ok) plan = shaped.plan;
  }
  return { presetId: preset.id, plan, focusRoomId: plan.rooms[0].id, plannerOpen: false, ui: NO_UI };
}

/** What was saved last time, unless it is unreadable or breaks a placement rule. */
function restoredState(): SandboxState {
  const saved = loadSandboxState();
  return saved && saved.plan.rooms.every((r) => validateRoom(r.surfaces).length === 0) ? saved : freshState(DEFAULT_PRESET_ID);
}

export const DesignerSandbox: React.FC = () => {
  // Read storage once; a preset switch or reset replaces the whole thing (and remounts the host).
  const [state, setState] = useState(restoredState);
  const [generation, setGeneration] = useState(0);
  const { variants: propVariants, catalogue, finishes, looks } = usePropLibrary();
  const replacePlan = useCallback((id: string) => {
    const next = freshState(id);
    saveSandboxState(next); // remembered at once, before any edit
    setState(next);
    setGeneration((g) => g + 1);
  }, []);

  const headerSlot = (
    <>
      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', margin: 0 }}>
        {S.preset}
        <select value={state.presetId} onChange={(e) => replacePlan(e.target.value)} style={controlStyle}>
          {DESIGNER_PRESETS.map((p) => (
            <option key={p.id} value={p.id} style={{ color: '#000' }}>{p.label}</option>
          ))}
          <option value={BLANK_PRESET_ID} style={{ color: '#000' }}>{S.blankPlan}</option>
        </select>
      </label>
      <IconTextButton icon={<RotateCcw size={13} />} label={S.resetRoom} onClick={() => replacePlan(state.presetId)} />
    </>
  );

  const headerEnd = (
    <Link to="/" className="dh-btn is-quiet" title={S.backToEstimator} aria-label={S.backToEstimator} style={{ textDecoration: 'none' }}>
      <ArrowLeft size={13} /> <span className="dh-label">{S.backToEstimator}</span>
    </Link>
  );

  return (
    <DesignerHost
      key={`${state.presetId}:${generation}`}
      initial={designerDocument(state.plan, state.focusRoomId)}
      initialPlannerOpen={state.plannerOpen}
      initialUi={state.ui}
      onChange={(doc, view) => saveSandboxState({ presetId: state.presetId, plan: doc.plan, focusRoomId: doc.focusRoomId, plannerOpen: view.plannerOpen, ui: view.ui })}
      title={S.sandboxTitle}
      headerSlot={headerSlot}
      headerEnd={headerEnd}
      propVariants={propVariants}
      catalogue={catalogue}
      finishes={finishes}
      looks={looks}
    />
  );
};

export default DesignerSandbox;
